import { DOMSerializer, Node as PMNode } from '@tiptap/pm/model';
import {
  TEMPLATE_FIELD_INLINE_NAME,
  TEMPLATE_FIELD_BLOCK_NAME,
  TEMPLATE_FIELD_DROPDOWN_NAME,
} from '../doc-engine/templateFields';
import { buildDropdownCommentValue } from '@shared/backendFrontend/dropdownCommentValue';
import { SELECTION_MARK_NAME } from '@modules/comments/constants';

/** fieldId → serialized HTML content string. */
export type TFieldValuesMap = Record<string, string>;

/**
 * Extract field values by serializing each template field's content to HTML.
 *
 * Doc-based: save REPLACES the stored field values with this map, so the caller has to pass the WHOLE
 * document - which for a large one is more than the editor holds.
 */
export const extractFieldValues = (doc: PMNode): TFieldValuesMap => {
  const fieldValues: TFieldValuesMap = {};
  const schema = doc.type.schema;
  const serializer = DOMSerializer.fromSchema(schema);
  const inlineType = schema.nodes[TEMPLATE_FIELD_INLINE_NAME];
  const blockType = schema.nodes[TEMPLATE_FIELD_BLOCK_NAME];
  const dropdownType = schema.nodes[TEMPLATE_FIELD_DROPDOWN_NAME];
  const wrapper = document.createElement('div');

  doc.descendants((node) => {
    if (node.type === dropdownType) {
      const fieldId = node.attrs.fieldId;
      if (!fieldId) {
        console.warn('[extractFieldValues] Dropdown field node found without fieldId, skipping');
        return false;
      }
      const selectedValue = node.attrs.selectedValue || '';
      const commentMark = node.marks.find((m) => m.type.name === SELECTION_MARK_NAME);
      fieldValues[fieldId] = commentMark
        ? buildDropdownCommentValue(selectedValue, commentMark.attrs.commentId)
        : selectedValue;
      return false;
    }
    if (node.type === inlineType || node.type === blockType) {
      const fieldId = node.attrs.fieldId;
      if (!fieldId) {
        console.warn('[extractFieldValues] Field node found without fieldId, skipping');
        return false;
      }

      wrapper.innerHTML = '';
      wrapper.appendChild(serializer.serializeFragment(node.content));
      fieldValues[fieldId] = wrapper.innerHTML;
      return false; // don't descend into field
    }
    return true;
  });

  return fieldValues;
};
